import { MapPinLineIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import { LocationPicker } from "@/features/search/components/LocationPicker";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { DestinationSearch } from "../components/DestinationSearch/DestinationSearch";
import type { HotelSearchForm } from "../HotelSearchForm";

type HotelDestinationPickerProps = {
  form: HotelSearchForm;
  analyticsSource: SearchSource;
  showErrorsInline?: boolean;
  onChange?: (values: HotelSearchFormInputValues) => void;
};

export function HotelDestinationPicker({
  form,
  analyticsSource,
  showErrorsInline,
  onChange,
}: HotelDestinationPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const destination = useWatch({ control: form.control, name: "destination" });

  return (
    <>
      <LocationPicker
        startIcon={<MapPinLineIcon />}
        label={intl.formatMessage({
          defaultMessage: "Destination",
          description: "Label for the destination input in hotel search",
        })}
        value={destination}
        shouldShowCode={false}
        onPickerOpen={() => {
          rudderAnalytics.track(
            HotelSearchTrackingEvents.USER_CLICKED_HOTEL_DESTINATION_INPUT,
            {
              source: analyticsSource,
            },
          );

          setIsDrawerOpen(true);
        }}
        errorMessage={
          showErrorsInline
            ? form.formState.errors.destination?.name?.message
            : undefined
        }
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
      >
        <DrawerTopBar
          onCloseClick={() => setIsDrawerOpen(false)}
          title={intl.formatMessage({
            defaultMessage: "Select a destination",
            description:
              "Placeholder text for the destination picker in hotel search",
          })}
        />

        <DrawerContent>
          <DestinationSearch
            onChange={(value) => {
              form.setValue("destination", value);
              onChange?.(form.getValues());
              setIsDrawerOpen(false);
            }}
            analyticsSource={analyticsSource}
          />
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
